


import React, { ReactNode } from 'react';

interface ErrorBoundaryProps {
    /**
     * The children components that the error boundary wraps around.
     * If any of these children components throw an error during rendering,
     * the error boundary will catch it.
     */
    children: ReactNode;

    /**
     * An optional fallback component or element to display when an error is caught.
     * If not provided, a default message ("Something went wrong.") will be shown.
     */
    fallback?: ReactNode;

    /**
     * An optional callback function that is triggered when an error is caught.
     * It receives the error and additional error information as its parameters.
     */
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
    /**
     * Indicates whether the error boundary caught an error.
     * If true, the error boundary will render the fallback UI.
     * If false, it will render its children normally.
     */
    hasError: boolean;
}

export class ErrorBoundaryBase extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            return this.props.fallback || <h1>Something went wrong.</h1>;
        }
        return this.props.children;
    }
}

